import { z } from "zod";

// Provenance of the weight on a set — the ONE definition.
//
// It used to be three: the TS union in `resolveDefaultWeight.ts`, the zod enum
// on `LogSet.weightSource`, and a second zod enum on the legacy nested
// `WorkoutSet`, whose comment said "Mirrors logSet.ts exactly" — which is a
// comment doing a type's job. Adding the effort values (PG-9) broke exactly
// there: two of the three were updated and the build caught the third. One
// definition, imported by all of them, is the fix (§CONSOLIDAMENTO).
//
// The values, and why the distinction is the point:
//   prescribed-absolute — the author typed a kg. Nothing was derived.
//   computed-percent    — a % of the athlete's MAX, resolved at plan-start.
//   history             — their last logged weight on the exercise, carried
//                         forward. A real number, but it answers "what did I
//                         lift" and not "what should I lift".
//   effort-from-*       — the load the app DERIVED so the athlete lands on a
//                         prescribed effort. Three of them, not one, because
//                         which instrument answered is what the number is
//                         worth: a single they really attempted (`tested`), a
//                         MAX trusted enough to propose from, or their own
//                         last set turned into an e1RM — weakest, and the only
//                         one needing no MAX at all.
//   manual              — the athlete typed it. Absence means this too, by
//                         convention: rows predate the field.
//
// ⭐ The effort values are the only ones a surface RENDERS: a derived load is
// a PROPOSAL and must say where it came from, never wear the face of a
// prescription (measurement-contract.md §17).
export const WeightSourceEnum = z.enum([
  "prescribed-absolute",
  "computed-percent",
  "history",
  "effort-from-tested-max",
  "effort-from-max",
  "effort-from-history",
  "manual",
]);
export type WeightSource = z.infer<typeof WeightSourceEnum>;

// Is this value one the app DERIVED to reach a prescribed effort? The three
// `effort-from-*` are the set that carries a visible provenance, so every
// surface asking "should I explain this number?" asks here rather than
// re-listing the literals — the habit that produced three copies of the enum.
export const isEffortDerivedWeightSource = (
  source: WeightSource | undefined,
): boolean => source !== undefined && source.startsWith("effort-from-");
